import {Injectable} from '@angular/core';
import {BehaviorSubject, finalize, forkJoin, Observable, of, switchMap, tap} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {DataManipulationService} from 'src/app/services/data-manipulation.service';
import {
  PyramidCategory,
  PyramidLegendItem
} from 'src/app/components/categories-pyramid-display/categories-pyramid-display.component';
import {TeamDynamicService} from '../services/team-dynamic.service';
import {TeamDynamicRequestDto} from '../dto/team-dynamic-request.dto';
import {
  ActiveTeamDynamicVm,
  ScoreDevelopmentChipVm,
  ScoreDevelopmentVm,
  ScoreDistributionSegmentVm,
  StatementResponsesVm,
  StatementsVm,
  StatementVm
} from '../models/team-dynamic.vm';
import {
  TeamHealthScoreVm,
  TeamIndicatorRowVm,
  TeamInsightVm,
  TeamReportHeaderVm
} from '../models/team-dynamic-team.vm';
import {
  mapActiveTeamDynamic,
  mapPyramidCategories,
  mapScoreDevelopment,
  mapScoreDevelopmentChips,
  mapScoreDistribution,
  mapStatements
} from '../mappers/team-dynamic.mapper';
import {
  mapTeamHealthScore,
  mapTeamIndicatorRows,
  mapTeamReportHeader
} from '../mappers/team-dynamic-team.mapper';

const EMPTY_SCORES: ScoreDevelopmentVm = {labels: [], values: []};

@Injectable({
  providedIn: 'root'
})
export class TeamDynamicTeamFacade {
  public intervals: any[] = [];

  private loadingSubject = new BehaviorSubject<boolean>(false);
  readonly loading$ = this.loadingSubject.asObservable();
  private headerSubject = new BehaviorSubject<TeamReportHeaderVm | null>(null);
  readonly header$ = this.headerSubject.asObservable();
  private healthScoreSubject = new BehaviorSubject<TeamHealthScoreVm | null>(null);
  readonly healthScore$ = this.healthScoreSubject.asObservable();
  private pyramidCategoriesSubject = new BehaviorSubject<PyramidCategory[]>([]);
  readonly pyramidCategories$ = this.pyramidCategoriesSubject.asObservable();
  private pyramidLegendSubject = new BehaviorSubject<PyramidLegendItem[]>([]);
  readonly pyramidLegend$ = this.pyramidLegendSubject.asObservable();
  private indicatorRowsSubject = new BehaviorSubject<TeamIndicatorRowVm[]>([]);
  readonly indicatorRows$ = this.indicatorRowsSubject.asObservable();
  private insightsSubject = new BehaviorSubject<TeamInsightVm[]>([]);
  readonly insights$ = this.insightsSubject.asObservable();
  private teamScoresSubject = new BehaviorSubject<ScoreDevelopmentVm>(EMPTY_SCORES);
  readonly teamScores$ = this.teamScoresSubject.asObservable();
  private workspaceScoresSubject = new BehaviorSubject<ScoreDevelopmentVm>(EMPTY_SCORES);
  readonly workspaceScores$ = this.workspaceScoresSubject.asObservable();
  private loadingScoresSubject = new BehaviorSubject<boolean>(false);
  readonly loadingScores$ = this.loadingScoresSubject.asObservable();
  private scoreChipsSubject = new BehaviorSubject<ScoreDevelopmentChipVm[]>([]);
  readonly scoreChips$ = this.scoreChipsSubject.asObservable();
  private openIndicatorUuidSubject = new BehaviorSubject<string | null>(null);
  readonly openIndicatorUuid$ = this.openIndicatorUuidSubject.asObservable();
  private indicatorScoresSubject = new BehaviorSubject<ScoreDevelopmentVm>(EMPTY_SCORES);
  readonly indicatorScores$ = this.indicatorScoresSubject.asObservable();
  private indicatorStatementsSubject = new BehaviorSubject<StatementsVm | null>(null);
  readonly indicatorStatements$ = this.indicatorStatementsSubject.asObservable();
  private loadingIndicatorSubject = new BehaviorSubject<boolean>(false);
  readonly loadingIndicator$ = this.loadingIndicatorSubject.asObservable();
  private loadingStatementsSubject = new BehaviorSubject<boolean>(false);
  readonly loadingStatements$ = this.loadingStatementsSubject.asObservable();
  private statementResponsesSubject = new BehaviorSubject<StatementResponsesVm | null>(null);
  readonly statementResponses$ = this.statementResponsesSubject.asObservable();
  private indicatorDistributionSubject = new BehaviorSubject<ScoreDistributionSegmentVm[]>([]);
  readonly indicatorDistribution$ = this.indicatorDistributionSubject.asObservable();
  private indicatorTeamsTotalSubject = new BehaviorSubject<number>(0);
  readonly indicatorTeamsTotal$ = this.indicatorTeamsTotalSubject.asObservable();
  private teamVisibilitySubject = new BehaviorSubject<boolean>(false);
  readonly teamVisibility$ = this.teamVisibilitySubject.asObservable();

  private activeTeamDynamic: ActiveTeamDynamicVm | null = null;

  constructor(private teamDynamicApi: TeamDynamicService, private data: DataManipulationService,
              private translate: TranslateService) {
  }

  loadData(teamUuid: string, payload: TeamDynamicRequestDto): void {
    this.reset();
    this.loadingSubject.next(true);
    this.setPyramidLegend();

    forkJoin({
      intervals: this.teamDynamicApi.getIndicatorInterval(),
      active: this.teamDynamicApi.getActiveWorkspaceTeamDynamics()
    }).pipe(
      switchMap((res: any) => {
        this.intervals = [...(res.intervals ?? [])];
        this.activeTeamDynamic = mapActiveTeamDynamic(res.active);
        this.teamVisibilitySubject.next(!!this.activeTeamDynamic?.teamVisibility);
        return this.loadReports(teamUuid, payload);
      }),
      tap((res: any) => this.pushReports(res.report, res.members, res.teamScores)),
      finalize(() => this.loadingSubject.next(false))
    ).subscribe();
  }

  reloadForPeriod(teamUuid: string, payload: TeamDynamicRequestDto): void {
    this.loadingScoresSubject.next(true);

    this.loadReports(teamUuid, payload).pipe(
      tap((res: any) => this.pushReports(res.report, res.members, res.teamScores)),
      finalize(() => this.loadingScoresSubject.next(false))
    ).subscribe();
  }

  loadScoreDevelopment(teamUuid: string, payload: TeamDynamicRequestDto): void {
    this.loadingScoresSubject.next(true);

    this.teamDynamicApi.getTeamTeamDynamicScores(teamUuid, this.getScoreOptions(payload)).pipe(
      finalize(() => this.loadingScoresSubject.next(false))
    ).subscribe((res: any) => this.pushScores(res));
  }

  openIndicator(teamUuid: string, collectionUuid: string, payload: TeamDynamicRequestDto): void {
    if (this.openIndicatorUuidSubject.value === collectionUuid) {
      return;
    }
    this.openIndicatorUuidSubject.next(collectionUuid);
    this.indicatorScoresSubject.next(EMPTY_SCORES);
    this.indicatorStatementsSubject.next(null);
    this.statementResponsesSubject.next(null);
    this.indicatorDistributionSubject.next([]);
    this.indicatorTeamsTotalSubject.next(0);
    this.loadingIndicatorSubject.next(true);

    const visible = !!this.activeTeamDynamic?.teamVisibility;

    forkJoin({
      scores: this.teamDynamicApi.getTeamTeamDynamicScores(teamUuid, {
        timedQuestionCollectionUuid: collectionUuid,
        startDate: payload.startDate,
        endDate: payload.endDate
      }),
      statements: this.teamDynamicApi.getQuestionTeamTeamDynamic(teamUuid,
        this.getStatementsOptions(collectionUuid, payload)),
      detail: visible
        ? this.teamDynamicApi.getWorkspaceTeamDynamicDetail(collectionUuid, payload.startDate, payload.endDate)
        : of(null)
    }).pipe(
      finalize(() => this.loadingIndicatorSubject.next(false))
    ).subscribe((res: any) => {
      this.indicatorScoresSubject.next(mapScoreDevelopment(res.scores));
      this.pushStatements(res.statements);
      this.pushDistribution(res.detail);
    });
  }

  private pushDistribution(response: any): void {
    const distribution = response && !response.error
      ? mapScoreDistribution(response.distribution, this.intervals, this.data.colors)
      : [];

    this.indicatorDistributionSubject.next(distribution);
    this.indicatorTeamsTotalSubject.next(
      distribution.reduce((total: number, segment: ScoreDistributionSegmentVm) => total + segment.teams, 0)
    );
  }

  closeIndicator(): void {
    this.openIndicatorUuidSubject.next(null);
    this.indicatorScoresSubject.next(EMPTY_SCORES);
    this.indicatorStatementsSubject.next(null);
    this.statementResponsesSubject.next(null);
    this.indicatorDistributionSubject.next([]);
    this.indicatorTeamsTotalSubject.next(0);
  }

  loadIndicatorStatements(teamUuid: string, payload: TeamDynamicRequestDto): void {
    const collectionUuid = this.openIndicatorUuidSubject.value;
    if (!collectionUuid) {
      return;
    }
    this.loadingStatementsSubject.next(true);
    this.statementResponsesSubject.next(null);

    this.teamDynamicApi.getQuestionTeamTeamDynamic(teamUuid,
      this.getStatementsOptions(collectionUuid, payload)).pipe(
      finalize(() => this.loadingStatementsSubject.next(false))
    ).subscribe((res: any) => this.pushStatements(res));
  }

  loadStatementResponses(teamUuid: string, statement: StatementVm): void {
    if (!statement?.questionUuid) {
      this.statementResponsesSubject.next(null);
      return;
    }
    this.statementResponsesSubject.next(null);

    const collectionUuid = this.openIndicatorUuidSubject.value;
    const request$ = statement.type === 'single'
      ? this.teamDynamicApi.getStatementScaledResponses(statement.questionUuid, collectionUuid, teamUuid)
      : statement.type === 'scaled'
        ? (statement.score === null
          ? null
          : this.teamDynamicApi.getStatementScaledResponses(statement.questionUuid, collectionUuid, teamUuid))
        : statement.type === 'open'
          ? this.teamDynamicApi.getStatementOpenResponses(statement.questionUuid, 1, 6, teamUuid)
          : null;

    if (!request$) {
      return;
    }

    request$.subscribe((res: any) => {
      this.statementResponsesSubject.next(res && !res.error ? {
        questionUuid: statement.questionUuid,
        type: statement.type,
        payload: res,
        totalItems: res._total_items ?? 0,
        pageCount: res._page_count ?? 0
      } : null);
    });
  }

  reset(): void {
    this.headerSubject.next(null);
    this.healthScoreSubject.next(null);
    this.pyramidCategoriesSubject.next([]);
    this.indicatorRowsSubject.next([]);
    this.scoreChipsSubject.next([]);
    this.insightsSubject.next([]);
    this.teamScoresSubject.next(EMPTY_SCORES);
    this.workspaceScoresSubject.next(EMPTY_SCORES);
    this.closeIndicator();
  }

  private pushStatements(response: any): void {
    this.indicatorStatementsSubject.next(mapStatements(response, this.intervals, this.data.colors));
  }

  private getStatementsOptions(collectionUuid: string, payload: TeamDynamicRequestDto) {
    return {
      timedQuestionCollectionUuid: collectionUuid,
      sort: payload.statementsSort,
      startDate: payload.statementsStartDate,
      endDate: payload.statementsEndDate
    };
  }

  private loadReports(teamUuid: string, payload: TeamDynamicRequestDto): Observable<any> {
    return forkJoin({
      report: this.teamDynamicApi.getTeamReportTeamDynamic(teamUuid, {
        startDate: payload.startDate,
        endDate: payload.endDate
      }),
      members: this.teamDynamicApi.getTeamMembers(teamUuid),
      teamScores: this.teamDynamicApi.getTeamTeamDynamicScores(teamUuid, this.getScoreOptions(payload))
    });
  }

  private pushReports(report: any, members: any, teamScores: any): void {
    const teamReport = report && !report.error ? report : null;
    const memberList = members && !members.error ? members : [];
    const categories = mapPyramidCategories(teamReport, this.intervals, this.data.colors);
    const healthScore = mapTeamHealthScore(teamReport, this.intervals, this.data.colors);

    this.headerSubject.next(mapTeamReportHeader(teamReport, memberList, this.data.baseUrl, ''));
    this.healthScoreSubject.next(healthScore);
    this.pyramidCategoriesSubject.next(categories);
    this.indicatorRowsSubject.next(mapTeamIndicatorRows(categories));
    this.scoreChipsSubject.next(this.buildScoreChips(categories, healthScore));
    this.pushScores(teamScores);
  }

  private pushScores(response: any): void {
    const scores = response && !response.error ? response : null;
    this.teamScoresSubject.next(mapScoreDevelopment(scores));
    this.workspaceScoresSubject.next(mapScoreDevelopment({scores: scores?.workspaceScores}));
  }

  private buildScoreChips(categories: PyramidCategory[],
                          healthScore: TeamHealthScoreVm | null): ScoreDevelopmentChipVm[] {
    if (!categories.length) {
      return [];
    }
    return mapScoreDevelopmentChips(categories, this.translate.instant('td_overall_score'), {
      score: healthScore?.score ?? 0,
      color: healthScore?.color ?? this.data.colors.grey,
      gradientColor: healthScore?.gradientColor ?? this.data.colors.greyGradient,
      trend: healthScore?.trend ?? null,
      diff: healthScore?.diff ?? null,
      teamsCount: 0
    });
  }

  private getScoreOptions(payload: TeamDynamicRequestDto) {
    return {
      startDate: payload.startDate,
      endDate: payload.endDate,
      timedQuestionCollectionUuid: payload.timedQuestionCollectionUuid ?? undefined
    };
  }

  private setPyramidLegend(): void {
    this.pyramidLegendSubject.next([
      {label: this.translate.instant('wo_team_dynamic_legend_strong'), color: this.data.colors.green},
      {label: this.translate.instant('wo_team_dynamic_legend_moderate'), color: this.data.colors.yellow},
      {label: this.translate.instant('wo_team_dynamic_legend_attention'), color: this.data.colors.orange},
      {label: this.translate.instant('wo_team_dynamic_legend_critical'), color: this.data.colors.red}
    ]);
  }
}
